// src/components/listing/ListingCard.js
// Carte d'annonce (grille) - compatible avec les données réelles de l'API - NE PAS MODIFIER

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useListings } from '../../hooks/useListings';
import { formatPrice } from '../../utils/formatPrice';

const ListingCard = ({ listing }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isFavorite, addToFavorites, removeFromFavorites } = useListings();

  if (!listing) return null;

  const { id, slug, title, quartier, city, price, images, bedrooms, bathrooms, area } = listing;
  // L'API renvoie price_type (snake_case), le repli priceType couvre d'anciennes données locales
  const priceType = listing.price_type || listing.priceType;

  const mainImage = images && images.length > 0 ? images[0] : '/images/placeholder.png';
  const favorite = isFavorite(id);

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (favorite) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  };

  const priceUnit = {
    perNight: '/ nuit',
    perMonth: '/ mois',
    perSquareMeter: '/ m²',
    service: '',
  }[priceType] || '';

  const styles = {
    card: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.bgCard,
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      border: `1px solid ${theme.border}`,
    },
    imageContainer: {
      position: 'relative',
      width: '100%',
      paddingTop: '66%',
      backgroundColor: theme.bgInput,
    },
    image: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    favoriteButton: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '15px',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '12px',
    },
    title: {
      fontSize: '14px',
      fontWeight: '600',
      color: theme.textPrimary,
      lineHeight: '1.3',
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    location: {
      fontSize: '12px',
      color: theme.textSecondary,
    },
    details: {
      fontSize: '12px',
      color: theme.textSecondary,
    },
    price: {
      fontSize: '14px',
      fontWeight: '700',
      color: theme.gold,
      marginTop: '2px',
    },
  };

  return (
    <div style={styles.card} onClick={() => navigate(`/listing/${slug}`)}>
      <div style={styles.imageContainer}>
        <img src={mainImage} alt={title} style={styles.image} />
        <button style={styles.favoriteButton} onClick={handleFavoriteClick}>
          {favorite ? '❤️' : '🤍'}
        </button>
      </div>
      <div style={styles.content}>
        <div style={styles.title}>{title}</div>
        <div style={styles.location}>📍 {quartier}, {city}</div>
        {(bedrooms || bathrooms || area) && (
          <div style={styles.details}>
            {bedrooms ? `${bedrooms} ch.` : ''}
            {bedrooms && bathrooms ? ' · ' : ''}
            {bathrooms ? `${bathrooms} sdb.` : ''}
            {(bedrooms || bathrooms) && area ? ' · ' : ''}
            {area ? `${area} m²` : ''}
          </div>
        )}
        <div style={styles.price}>
          {formatPrice(price)} {priceUnit}
        </div>
      </div>
    </div>
  );
};

export default ListingCard;